import type {
  ScanListRequest,
  SponsoredListInput,
  SuggestItemsRequest,
} from '@lista/shared-validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCore } from '../context';
import { queryKeys } from '../query-keys';

/** Sponsored lists for the country and city on the profile (empty without a country). */
export function useSponsoredLists(enabled = true) {
  const { services, locale } = useCore();
  return useQuery({
    queryKey: [...queryKeys.sponsored, 'feed', locale],
    queryFn: services.sponsored.list,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSponsoredList(id: string | null) {
  const { services, locale } = useCore();
  return useQuery({
    queryKey: [...queryKeys.sponsored, 'one', id, locale],
    queryFn: () => services.sponsored.get(id!),
    enabled: !!id,
  });
}

/**
 * Admin list. A 403 means "not an admin": the query does not retry, so apps can use
 * `isSuccess` to decide whether to show the admin entry (the API still decides everything).
 */
export function useAdminSponsoredLists() {
  const { services, locale } = useCore();
  return useQuery({
    queryKey: [...queryKeys.sponsoredAdmin, locale],
    queryFn: services.sponsored.admin.list,
    retry: false,
    staleTime: 60_000,
  });
}

function useInvalidateSponsored() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: queryKeys.sponsored });
}

export function useSaveSponsoredList() {
  const { services } = useCore();
  const invalidate = useInvalidateSponsored();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: SponsoredListInput }) =>
      id ? services.sponsored.admin.replace(id, body) : services.sponsored.admin.create(body),
    onSuccess: invalidate,
  });
}

export function useDeleteSponsoredList() {
  const { services } = useCore();
  const invalidate = useInvalidateSponsored();
  return useMutation({
    mutationFn: (id: string) => services.sponsored.admin.remove(id),
    onSuccess: invalidate,
  });
}

/** Asks for proposed items. Nothing is saved: the caller adds the chosen ones to a list. */
export function useSuggestItems() {
  const { services } = useCore();
  return useMutation({ mutationFn: (body: SuggestItemsRequest) => services.ai.suggest(body) });
}

/** Which AI features the server offers; "Scan a paper list" needs `listScan`. */
export function useAiCapabilities(enabled = true) {
  const { services } = useCore();
  return useQuery({
    queryKey: ['ai', 'capabilities'],
    queryFn: () => services.ai.capabilities(),
    staleTime: 60 * 60_000,
    enabled,
  });
}

/** ADR-0022: reads a photo of a paper list. Nothing is saved; add the approved lines after. */
export function useScanList() {
  const { services } = useCore();
  return useMutation({ mutationFn: (body: ScanListRequest) => services.ai.scanList(body) });
}
